"use client";

/* eslint @next/next/next-script-for-ga: 0 */
import Head from "next/head";
import { usePathname } from "next/navigation";
import { memo, useEffect } from "react";

import { KEYS } from "@/src/const";
import { TrackingLogic } from "@/src/logic";
import { StrapiCollectionType, StrapiComponentType } from "@/src/type";
import { FrontendTrackingService } from "@/src/service";
import { Log } from "@/src/util";

interface TrackingProps {
  tracking: StrapiComponentType.Object.Tracking;
  marketingSources: StrapiCollectionType.MarketingSource[];
}

function Tracking({ tracking, marketingSources }: TrackingProps) {
  const pathname = usePathname();

  /**
   * listen to path changes and log the new page view
   */
  useEffect(() => {
    //set the google ads conversion id and label
    if (tracking.googleAdsConversion && tracking.googleAds) {
      localStorage.setItem(KEYS.LOCAL_STORAGE.CONVERSION_ID, tracking.googleAds);
      localStorage.setItem(KEYS.LOCAL_STORAGE.CONVERSION_LABEL, tracking.googleAdsConversion);
    }

    //get / set the utm / tracking params
    const utmParams = TrackingLogic.getUTMParams();

    //set the marketing source
    const marketingSource = TrackingLogic.setMarketingSource(utmParams, marketingSources);

    //log the page event
    FrontendTrackingService.postTrackingLog({
      action: "page_view",
      actionMessage: "Pathname change listener",
      triggeredFrom: "Tracking.tsx:(useEffect pathname change listener)",
    });

    Log.logToConsoleDebug(
      "partial/Tracking.tsx",
      "Tracking.tsx:(useEffect pathname change listener)",
      [{ pathname, utmParams, marketingSources, tracking, marketingSource }],
      Log.LOG_COLORS.STATE_CHANGE.BG_COLOR,
      Log.LOG_COLORS.STATE_CHANGE.COLOR
    );
  }, [pathname, tracking, marketingSources]);

  return (
    <Head>
      <script
        async
        defer
        dangerouslySetInnerHTML={{
          __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${tracking.googleTagManager}');`,
        }}
      ></script>
      {tracking.googleAnalytics && (
        <>
          <script
            async
            defer
            src={`https://www.googletagmanager.com/gtag/js?id=${tracking.googleAnalytics}`}
          ></script>
          <script
            async
            defer
            dangerouslySetInnerHTML={{
              __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${tracking.googleAnalytics}');
            `,
            }}
          ></script>
        </>
      )}
      {tracking.googleAds && (
        <script
          async
          defer
          src={`https://www.googletagmanager.com/gtag/js?id=${tracking.googleAds}`}
        ></script>
      )}
      {tracking.facebookPixel && (
        <script
          async
          defer
          dangerouslySetInnerHTML={{
            __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', ${tracking.facebookPixel});
              fbq('track', 'PageView');
              `,
          }}
        ></script>
      )}
      {tracking.followUpBoss && (
        <script
          async
          defer
          dangerouslySetInnerHTML={{
            __html: `
          (function(w,i,d,g,e,t){w["WidgetTrackerObject"]=g;(w[g]=w[g]||function() {(w[g].q=w[g].q||[]).push(arguments);}),(w[g].ds=1*new Date());(e="script"), (t=d.createElement(e)),(e=d.getElementsByTagName(e)[0]);t.async=1;t.src=i; e.parentNode.insertBefore(t,e);}) (window,"https://widgetbe.com/agent",document,"widgetTracker"); window.widgetTracker("create", "${tracking.followUpBoss}"); window.widgetTracker("send", "pageview")
            `,
          }}
        ></script>
      )}
      {tracking.hubspot && (
        <>
          <script
            dangerouslySetInnerHTML={{
              __html: `
                window._hsq = window._hsq || [];
                window._hsq.push(['setConfig', {
                  forms: {
                    formDetect: false
                  }
                }]);
              `,
            }}
          ></script>
          <script
            async
            defer
            type="text/javascript"
            id="hs-script-loader"
            src={`https://js-na2.hs-scripts.com/${tracking.hubspot}.js`}
          ></script>
        </>
      )}
    </Head>
  );
}

const MemorizedTracking = memo(Tracking);
export default MemorizedTracking;
